import { type ClassValue, clsx } from "clsx";
import { ForwardRefExoticComponent, RefAttributes, SVGProps } from "react";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { PlayerMode, PlayerPresetSettings } from "@yedalabs/schema";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export type HeroIcon = ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & {
        title?: string;
        titleId?: string;
    } & RefAttributes<SVGSVGElement>
>;

export const formatIsoStringDate = (
    isoStringDate: string,
    includeTime?: boolean
) => {
    return new Date(isoStringDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        ...(includeTime && { hour: "2-digit", minute: "2-digit" }),
    });
};

export const formatSecondsToMinutes = (numOfSeconds: number) => {
    const hours = Math.floor(numOfSeconds / 3600);
    const minutes = Math.floor((numOfSeconds % 3600) / 60);
    const seconds = Math.floor(numOfSeconds % 60);

    if (hours > 0) {
        return `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }

    return `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
};

export function getFileType(mimeType: string): string {
    const typeParts = mimeType.split("/");
    return typeParts.length > 1 ? typeParts[1] : typeParts[0];
}

export const PRIMARY_COLOR = "#000F61";

export const isWhite = (color: string) =>
    color === "#fff" ||
    color === "#FFF" ||
    color === "#ffffff" ||
    color === "#FFFFFF";
export const isBlack = (color: string) =>
    color === "#000" || color === "#000000";

export const HOMEPAGE = "/home";

export const datadogEnabled = process.env.ENV !== "local";

export const getVideoEmbedUrl = (pullZone: string, videoId: string) => {
    if (process.env.NEXT_PUBLIC_ENV === "prod") {
        return `https://iframe.yedatech.io/embed/${pullZone}/${videoId}`;
    } else if (process.env.NEXT_PUBLIC_ENV === "dev") {
        return `https://dev.iframe.yedatech.io/embed/${pullZone}/${videoId}`;
    } else {
        return `http://localhost:3002/embed/${pullZone}/${videoId}`;
    }
};

export const getChatbotScript = (chatbotId: string, version?: "v1" | "v2") => {
    const scriptSrc = getChatbotScriptSrc(version);
    const embedCode = `<script src="${scriptSrc}" data-env="${process.env.NEXT_PUBLIC_ENV}" data-chatbot-id="${chatbotId}" async></script>`;
    return embedCode;
};

export const getChatbotScriptSrc = (version?: "v1" | "v2") => {
    const suffix = version === "v2" ? "v2/embed.js" : "embed.js";

    if (process.env.NEXT_PUBLIC_ENV === "prod") {
        return `https://chat.yedalabs.io/${suffix}`;
    } else if (process.env.NEXT_PUBLIC_ENV === "dev") {
        return `https://dev.chat.yedalabs.io/${suffix}`;
    } else {
        return `http://localhost:3003/${suffix}`;
    }
};

export const getChatbotFaqIframeSrc = () => {
    const suffix = `v2/faq`;

    if (process.env.NEXT_PUBLIC_ENV === "prod") {
        return `https://chat.yedalabs.io/${suffix}`;
    } else if (process.env.NEXT_PUBLIC_ENV === "dev") {
        return `https://dev.chat.yedalabs.io/${suffix}`;
    } else {
        return `http://localhost:3003/${suffix}`;
    }
};

export const getChatbotTranscriptIframeSrc = () => {
    const suffix = `v2/transcript`;

    if (process.env.NEXT_PUBLIC_ENV === "prod") {
        return `https://chat.yedalabs.io/${suffix}`;
    } else if (process.env.NEXT_PUBLIC_ENV === "dev") {
        return `https://dev.chat.yedalabs.io/${suffix}`;
    } else {
        return `http://localhost:3003/${suffix}`;
    }
};

type PlayerQueryOptions = Partial<PlayerPresetSettings> & { mode?: PlayerMode };

const buildPlayerQuery = (opts?: PlayerQueryOptions): string => {
    if (!opts) return "";
    const params: [string, string][] = [];
    if (opts.autoPlay) params.push(["autoPlay", "true"]);
    if (opts.muted) params.push(["muted", "true"]);
    if (opts.captions) params.push(["captions", "true"]);
    if (opts.disableSeeking) params.push(["disableSeeking", "true"]);
    if (opts.englishPlayer) params.push(["lang", "en"]);
    if (opts.mode === "lomda") params.push(["mode", "lomda"]);
    if (params.length === 0) return "";
    return "?" + params.map(([k, v]) => `${k}=${v}`).join("&");
};

export const getPlatformBaseUrl = () =>
    process.env.NEXT_PUBLIC_ENV === "local"
        ? "http://localhost:3000"
        : process.env.NEXT_PUBLIC_ENV === "dev"
          ? "https://dev.app.yedalabs.io"
          : "https://app.yedalabs.io";

export const getDirectPlayUrl = (
    pullZone: string,
    videoId: string,
    opts?: PlayerQueryOptions
) =>
    `${getPlatformBaseUrl()}/play/${pullZone}/${videoId}${buildPlayerQuery(opts)}`;

export const getVideoEmbedUrlWithOptions = (
    pullZone: string,
    videoId: string,
    opts?: PlayerQueryOptions
) => `${getVideoEmbedUrl(pullZone, videoId)}${buildPlayerQuery(opts)}`;

export const getBunnyDownloadUrl = ({
    videoId,
    storageZoneName,
    storageZoneHostname,
    storageZonePassword,
    fileName,
}: {
    videoId: string;
    storageZoneName: string;
    storageZoneHostname: string;
    storageZonePassword: string;
    fileName: string;
}) => {
    return `https://${storageZoneHostname}/${storageZoneName}/${videoId}/${fileName}?accessKey=${storageZonePassword}&download`;
};

export const getVideoThumbnailUrl = (videoId: string) => {
    return `${process.env.R2_PUBLIC_BUCKET_URL}/videos/${videoId}/thumbnails/thumbnail`;
};

export const address = "יגאל אלון 123, תל אביב";
export const tosLink = "https://yedalabs.ai/terms";

export const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return "לפני פחות מדקה";
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return diffInMinutes === 1 ? "לפני דקה" : `לפני ${diffInMinutes} דקות`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return diffInHours === 1 ? "לפני שעה" : `לפני ${diffInHours} שעות`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
        return diffInDays === 1 ? "לפני יום" : `לפני ${diffInDays} ימים`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
        return diffInWeeks === 1 ? "לפני שבוע" : `לפני ${diffInWeeks} שבועות`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return diffInMonths === 1 ? "לפני חודש" : `לפני ${diffInMonths} חודשים`;
    }

    const diffInYears = Math.floor(diffInDays / 365);
    return diffInYears === 1 ? "לפני שנה" : "לפני יותר משנה";
};

export type DecimalUnit = "KB" | "MB" | "GB" | "TB";
export const units: DecimalUnit[] = ["KB", "MB", "GB", "TB"];

const multipliers: Record<DecimalUnit, number> = {
    KB: 1e3,
    MB: 1e6,
    GB: 1e9,
    TB: 1e12,
};

/** Get how many bytes in a unit. */
export function bytesPerUnit(unit: DecimalUnit): number {
    return multipliers[unit];
}

/** Convert a byte count to a decimal unit (KB/MB/GB/TB). */
export function convertBytes(bytes: number, to: DecimalUnit): number {
    return bytes / bytesPerUnit(to);
}

export function convertToBytes(value: number, unit: DecimalUnit): number {
    return value * bytesPerUnit(unit);
}

export function formatFileSize(bytes: number): string {
    const sign = bytes < 0 ? "-" : "";
    const absBytes = Math.abs(bytes);
    for (let i = 0; i < units.length; i++) {
        const value = convertBytes(absBytes, units[i]);
        if (value < 1000 || i === units.length - 1) {
            return `${sign}${value.toFixed(2)} ${units[i]}`;
        }
    }
    return `${sign}${absBytes} B`; // fallback, only for < 1 KB
}

export const chunkArray = <T>(arr: T[], size: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
        out.push(arr.slice(i, i + size));
    }
    return out;
};

export const zFile = z.object({
    url: z.string(),
    type: z.enum(["pdf", "powerpoint"]),
});

export const zImageFile = z.object({
    url: z.string(),
    type: z.enum(["png", "svg", "jpg", "jpeg", "webp", "gif", "bmp", "tiff"]),
});

export type File = z.infer<typeof zFile>;
export type ImageFile = z.infer<typeof zImageFile>;

export const mimeTypeToFileTypePdfPpt = (
    mimeType: string
): File["type"] | null => {
    if (mimeType === "application/pdf") return "pdf";
    if (
        mimeType === "application/vnd.ms-powerpoint" ||
        mimeType ===
            "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    )
        return "powerpoint";
    return null;
};

export const mimeTypeToFileTypeImages = (
    mimeType: string
): ImageFile["type"] | null => {
    const mimeToType: Record<string, ImageFile["type"]> = {
        "image/png": "png",
        "image/svg+xml": "svg",
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/webp": "webp",
        "image/gif": "gif",
        "image/bmp": "bmp",
        "image/tiff": "tiff",
    };
    return mimeToType[mimeType] || null;
};
